/**
 * The checkout acknowledgments as ONE checkbox with the full legal text one
 * tap away — instead of five dense paragraphs, each with its own box, as the
 * last thing before the pay button.
 *
 * Nothing legal changed: the five acknowledgment texts
 * (checkoutAcknowledgmentsContent.jsx) are verbatim, the Terms of Service and
 * Privacy Policy links still open the full documents, and the customer still
 * affirmatively accepts every item — the single box sets all five flags at
 * once in Review.jsx, so the payment gate is unchanged. The expander is
 * collapsed by default and opens inline (no navigation, no modal).
 *
 * While the Faye launch flag is on, the cancellation and default items carry
 * the conditional travel insurance carve-outs whether or not the customer adds
 * a plan, so adding one never unticks this box; still five items, one box.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/activityTracker';
import { acknowledgmentItems, renderDocLink } from './checkoutAcknowledgmentsContent';

export default function CheckoutAcknowledgments({ accepted, onAcceptedChange, onOpenDocument, requiredMessage = null, className }) {
  const [expanded, setExpanded] = useState(false);
  const items = acknowledgmentItems(onOpenDocument);

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) trackEvent('checkout_terms_expanded');
  };

  return (
    <div className={cn('mb-8 text-left max-w-2xl mx-auto', className)}>
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox
          id="review-acknowledgments"
          checked={accepted}
          onCheckedChange={(checked) => {
            const next = !!checked;
            onAcceptedChange(next);
            trackEvent('checkout_terms_accepted', { accepted: next });
          }}
          className="border-white/30 data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600 mt-0.5 flex-shrink-0 h-5 w-5"
        />
        <span className="text-white/75 text-[13px] font-normal leading-snug">
          I have read and agree to the five acknowledgments below, the{' '}
          {renderDocLink('terms', onOpenDocument, 'Terms of Service')}
          {' '}and the{' '}
          {renderDocLink('privacy', onOpenDocument, 'Privacy Policy')}.
        </span>
      </label>

      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        aria-controls="review-acknowledgments-text"
        className="mt-2 ml-8 inline-flex items-center gap-1 text-[12px] font-normal text-white/50 hover:text-white/75 transition-colors"
      >
        {expanded ? 'Hide the full terms' : `Read the full terms (${items.length} items)`}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-200', expanded && 'rotate-180')} aria-hidden />
      </button>

      {expanded && (
        <ol id="review-acknowledgments-text" className="mt-3 ml-8 space-y-3 list-decimal pl-4">
          {items.map((item) => (
            <li key={item.id} className="text-white/45 text-[12px] font-normal leading-snug">
              <strong className="font-medium text-white/65">{item.title}</strong>{' '}
              {item.body}
            </li>
          ))}
        </ol>
      )}

      {requiredMessage && (
        <p className="mt-3 px-4 py-3 bg-brand-900/40 border border-brand-800/35 text-brand-300 text-sm font-normal rounded-lg">
          {requiredMessage}
        </p>
      )}
    </div>
  );
}

CheckoutAcknowledgments.propTypes = {
  accepted: PropTypes.bool.isRequired,
  onAcceptedChange: PropTypes.func.isRequired,
  onOpenDocument: PropTypes.func.isRequired,
  requiredMessage: PropTypes.string,
  className: PropTypes.string,
};
